import { Skeleton } from 'antd';
import { push } from 'connected-react-router';
import { selectLoading } from 'containers/HomePage/selectors';
import { ILoadingSection } from 'containers/HomePage/types';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import 'swiper/css';
import 'swiper/css/navigation';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Product, ProductSortField, SortDirection } from 'types/schema';
import { commonDimensions } from 'utils/constants';
import DesktopBg from './desktopBg.png';
import messages from './messages';
import MobileBg from './mobileBg.png';
import { RootState } from './types';

import useProductUomInfo from 'hooks/useProductUomInfo';
import { Link } from 'react-router-dom';
import calculateVAT from 'utils/calculateVAT';
import formatMoney from 'utils/formatMoney';
import generateProductSlug from 'utils/generateProductSlug';
import { createCache } from 'utils/moduleCache';
import { GQuery, useLazyQuerySaga } from '../../hooks/useQuerySaga';
import { QueryProductSearchArgs } from '../../types/schema';
import { productSearch } from '../../utils/query';

/* ─── Layout ─────────────────────────────────────────────────────────────────── */
const SectionContainer = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
  padding: 0 40px !important;
  @media (max-width: 1023px) {
    padding: 0 16px !important;
  }
  @media (max-width: 767px) {
    padding: 0px !important;
    margin-bottom: 12px;
  }
`;

/* ─── Card ────────────────────────────────────────────────────────────────────── */
interface CardProps {
  isEssential?: boolean;
}

const Card = styled.div<CardProps>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 16px;
  padding: 32px;
  gap: 0;
  min-height: 317px;
  overflow: hidden;
  justify-content: space-between;
  background: url(${DesktopBg});
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  @media (max-width: 1023px) {
    flex-direction: column;
    padding: 24px 16px;
    gap: 24px;
    min-height: auto;
    align-items: flex-start;
    background: url(${MobileBg});
    background-size: cover;
    background-repeat: no-repeat;
  }
  @media (max-width: 767px) {
    border-radius: 0px;
  }
`;

/* ─── Info Panel ──────────────────────────────────────────────────────────────── */
const InfoPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 345px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  @media (max-width: 1023px) {
    width: 100%;
  }
`;

const SectionTitle = styled.h2<CardProps>`
  font-size: 40px;
  font-weight: 700;
  line-height: 52px;
  color: #2f7434;
  margin: 0;
  @media (max-width: 767px) {
    font-size: 30px;
    line-height: 40px;
  }
`;

const SectionSubTitle = styled.p`
  font-size: 30px;
  line-height: 40px;
  @media (max-width: 767px) {
    font-size: 20px;
    line-height: 28px;
  }
`;

const SectionDescription = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #4b5563;
  margin: 0;
  @media (max-width: 767px) {
    font-size: 12px;
    line-height: 18px;
    color: #111827;
  }
`;

const TagsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Tag = styled.div<CardProps>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 100px;
  background: #def6bb;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  color: #2f7434;
  white-space: nowrap;
`;

const CtaBtn = styled(Link)<CardProps>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #43a047;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  padding: 8px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  width: fit-content;
  transition: opacity 0.2s;
  &:hover {
    color: #ffffff;
    opacity: 0.9;
  }
`;

/* ─── Swiper area ─────────────────────────────────────────────────────────────── */
const SwiperWrapper = styled.div<CardProps>`
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
  max-width: 542px;
  .swiper {
    width: 100%;
    padding: 24px 16px !important;
  }
  .swiper-button-next,
  .swiper-button-prev {
    width: 40px !important;
    height: 40px !important;
    border-radius: 50%;
    background: #ffffff !important;
    // border: 1px solid #e5e7eb;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    // box-shadow: 2px 6px 32px 0 rgba(112, 112, 112, 0.15);
    top: 47% !important;
    &::after {
      font-size: 13px !important;
      color: #4b5563 !important;
      font-weight: 800;
    }
    &.swiper-button-disabled {
      opacity: 0 !important;
      pointer-events: none;
    }
  }
  .swiper-button-next {
    right: 2px;
  }
  .swiper-button-prev {
    left: 2px;
  }
  @media (max-width: 1023px) {
    margin: 0 auto;
    .swiper {
      padding: 0 !important;
    }
  }
  @media (max-width: 767px) {
    max-width: 100%;
    width: 100%;
    .swiper-button-next,
    .swiper-button-prev {
      display: none;
    }
  }
`;

const StyledSwiperSlide = styled(SwiperSlide)`
  width: 160px !important;
  height: auto !important;
  @media (max-width: 767px) {
    width: 140px !important;
  }
`;

/* ─── Product item wrapper ────────────────────────────────────────────────────── */
const ProductItemWrapper = styled(Link)`
  width: 160px;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
  // box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  height: 232px;
  display: block;
  @media (max-width: 767px) {
    width: 140px;
    height: 210px;
  }
`;

const ProductImage = styled.img`
  width: 160px;
  height: 160px;
  object-fit: cover;
  display: block;
  background: rgb(255, 255, 255);
  @media (max-width: 767px) {
    width: 140px;
    height: 140px;
  }
`;

const ProductInfoWrapper = styled.div`
  padding: 0 12px 12px;
`;

const ProductPrice = styled.span`
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  color: #111827;
  @media (max-width: 767px) {
    font-size: 14px;
    line-height: 22px;
  }
`;

const ProductName = styled.div`
  color: #111827;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  @media (max-width: 767px) {
    font-size: 11px;
    line-height: 16px;
  }
`;

const ProductUom = styled.span`
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  color: #6b7280;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-top: -4px;
  & > div > div {
    color: rgb(107, 114, 128);
    font-size: 10px !important;
    margin: 0px !important;
    background: none;
    padding: 0px;
    & > div {
      display: none;
    }
  }
  @media (max-width: 767px) {
    font-size: 10px;
    line-height: 14px;
  }
`;

/* ─── Icons ───────────────────────────────────────────────────────────────────── */
const ArrowIcon: FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.9165 7H11.0832M11.0832 7L7.58317 3.5M11.0832 7L7.58317 10.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PinIcon: FC<{ color?: string }> = ({ color = '#2F7434' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M10 5C10 7.4965 7.2305 10.0965 6.3005 10.8995C6.21386 10.9646 6.1084 10.9999 6 10.9999C5.8916 10.9999 5.78614 10.9646 5.6995 10.8995C4.7695 10.0965 2 7.4965 2 5C2 3.93913 2.42143 2.92172 3.17157 2.17157C3.92172 1.42143 4.93913 1 6 1C7.06087 1 8.07828 1.42143 8.82843 2.17157C9.57857 2.92172 10 3.93913 10 5Z"
      stroke="#2F7434"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M6 6.5C6.82843 6.5 7.5 5.82843 7.5 5C7.5 4.17157 6.82843 3.5 6 3.5C5.17157 3.5 4.5 4.17157 4.5 5C4.5 5.82843 5.17157 6.5 6 6.5Z"
      stroke="#2F7434"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

const CheckCircleIcon: FC<{ color?: string }> = ({ color = '#2F7434' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M10 6.50022C10 9.00022 8.25 10.2502 6.17 10.9752C6.06108 11.0121 5.94277 11.0104 5.835 10.9702C3.75 10.2502 2 9.00022 2 6.50022V3.00022C2 2.86762 2.05268 2.74044 2.14645 2.64667C2.24021 2.5529 2.36739 2.50022 2.5 2.50022C3.5 2.50022 4.75 1.90022 5.62 1.14022C5.72593 1.04972 5.86068 1 6 1C6.13932 1 6.27407 1.04972 6.38 1.14022C7.255 1.90522 8.5 2.50022 9.5 2.50022C9.63261 2.50022 9.75979 2.5529 9.85355 2.64667C9.94732 2.74044 10 2.86762 10 3.00022V6.50022Z"
      stroke="#2F7434"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path d="M4.5 6L5.5 7L7.5 5" stroke="#2F7434" stroke-linecap="round" stroke-linejoin="round" />
  </svg>
);

/* ─── Skeleton ──────────────────────────────────────────────────────────────────── */
const SkeletonSlide: FC = () => (
  <div
    style={{ borderRadius: 12, overflow: 'hidden', background: '#ffffff', display: 'flex', flexDirection: 'column' }}
  >
    <Skeleton.Input active block style={{ height: 160, borderRadius: 0 }} />
    <div style={{ padding: '8px 12px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Skeleton.Input active block size="small" style={{ width: 80 }} />
      <Skeleton.Input active block size="small" style={{ width: '100%' }} />
    </div>
  </div>
);

/* ─── ProductSlide ────────────────────────────────────────────────────────────── */
const ProductSlide: FC<{ product: Product; lang: string | null }> = ({ product, lang }) => {
  const { uomInfo } = useProductUomInfo(product);
  return (
    <ProductItemWrapper to={`/products/${generateProductSlug(product?.name, product?.supplierInternalCode, lang)}`}>
      <ProductImage src={product.imageUrl || ''} alt={product.name || ''} />
      <ProductInfoWrapper>
        <ProductPrice>
          {formatMoney(
            calculateVAT(
              product?.priceV2?.originalPrice || 0,
              product?.priceV2?.vat || 0,
              product?.priceV2?.vatIncluded || false,
            ).beforeVat,
          )}
          ₫
        </ProductPrice>
        <ProductName>{product.name}</ProductName>
        {product?.uom && <ProductUom>{uomInfo}</ProductUom>}
      </ProductInfoWrapper>
    </ProductItemWrapper>
  );
};

/* ─── Interfaces ──────────────────────────────────────────────────────────────── */
interface IOwnProps {
  isProductDetail?: boolean;
}

interface StateProps {
  loading: ILoadingSection | boolean;
}

interface DispatchProps {
  gotoSignIn: () => void;
}

type IProps = StateProps & DispatchProps & IOwnProps;

/* ─── Module-level cache ─────────────────────────────────────────────────────── */
const _farmCache = createCache<Product[]>(10 * 60 * 1000);

/* ─── KamereoProduct ──────────────────────────────────────────────────────────── */
const KamereoProduct: FC<IProps> = (props) => {
  const lang = window.localStorage.getItem('lang');
  const [productData, setProductData] = useState([] as any[]);
  const [isFetching, setIsFetching] = useState(!_farmCache.get());

  const [fetch] = useLazyQuerySaga<GQuery<'productSearch'>, QueryProductSearchArgs>(productSearch, {
    authentication: false,
  });

  useEffect(() => {
    const cached = _farmCache.get();
    if (cached) {
      setProductData(cached);
      setIsFetching(false);
      return;
    }
    (async () => {
      const [lectureData, vegetData, mushroomData] = await Promise.all([
        fetch({
          variables: {
            filter: {
              tags: ['VEGETABLE_FARM'],
            },
            sort: [
              {
                field: ProductSortField.CreatedAt,
                order: SortDirection.Desc,
              },
            ],
            pagination: { page: 0, size: 3 },
          },
        }),
        fetch({
          variables: {
            filter: {
              tags: ['LETTUCE_FARM'],
            },
            sort: [
              {
                field: ProductSortField.CreatedAt,
                order: SortDirection.Desc,
              },
            ],
            pagination: { page: 0, size: 3 },
          },
        }),
        fetch({
          variables: {
            filter: {
              tags: ['MUSHROOM_FARM'],
            },
            sort: [
              {
                field: ProductSortField.CreatedAt,
                order: SortDirection.Desc,
              },
            ],
            pagination: { page: 0, size: 3 },
          },
        }),
      ]);
      const combined = [
        ...(lectureData?.data || []),
        ...(vegetData?.data || []),
        ...(mushroomData?.data || []),
      ] as Product[];
      _farmCache.set(combined);
      setProductData(combined);
      setIsFetching(false);
    })();
  }, []);

  return (
    <SectionContainer id="INTRODUCE_HP">
      <Card>
        {/* Left: info panel */}
        <InfoPanel>
          <SectionTitle>
            {lang === 'vi' ? (
              <>
                Từ nông trại <SectionSubTitle>đến doanh nghiệp</SectionSubTitle>
              </>
            ) : (
              <>Farm to Business</>
            )}
          </SectionTitle>

          <SectionDescription>
            <FormattedMessage {...messages.freshDescription} />
          </SectionDescription>

          <TagsRow>
            <Tag>
              <PinIcon />
              <FormattedMessage {...messages.freshTag1} />
            </Tag>
            <Tag>
              <CheckCircleIcon />
              <FormattedMessage {...messages.freshTag2} />
            </Tag>
          </TagsRow>

          <CtaBtn to={`/tu-nong-trai-den-doanh-nghiep?lang=${lang}`}>
            <FormattedMessage {...messages.exploreCta} />
            <ArrowIcon />
          </CtaBtn>
        </InfoPanel>
        <SwiperWrapper>
          <Swiper
            modules={[Navigation, Mousewheel]}
            slidesPerView="auto"
            spaceBetween={16}
            navigation
            mousewheel={{ forceToAxis: true }}
            simulateTouch
          >
            {isFetching
              ? Array.from({ length: 6 }).map((_, i) => (
                  <StyledSwiperSlide key={`skeleton-${i}`}>
                    <SkeletonSlide />
                  </StyledSwiperSlide>
                ))
              : (productData as Product[]).map((product: Product, index: number) => (
                  <StyledSwiperSlide key={product?.id || index}>
                    <ProductSlide product={product} lang={lang} />
                  </StyledSwiperSlide>
                ))}
          </Swiper>
        </SwiperWrapper>
      </Card>
    </SectionContainer>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  loading: selectLoading(),
});

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    gotoSignIn: () => dispatch(push('/signin')),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(injectIntl(KamereoProduct));
